import {
  Button, FormControl, FormControlLabel, FormHelperText,
  MenuItem, Select, Switch, Typography,
} from '@material-ui/core'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import AddIcon from '@material-ui/icons/Add'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'

import {
  ContainerProps, CONTAINER_HEIGHT,
  CONTAINER_WIDTH, useContainer
} from '../hooks/useContainer'
import { GRID_SIZE } from '../services/gameGenerator'

import { ResponsiveDialog } from './Dialog'
import { Grid } from './Grid'

const createStyle = (rowNumber: number, columnNumber: number) => {
  const gridTemplateRows = `repeat(${rowNumber}, 1fr)`
  const gridTemplateColumns = `repeat(${columnNumber}, 1fr)`

  return makeStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    controlWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      width: CONTAINER_WIDTH,
      maxWidth: '95%',
      marginTop: 16,
    },
    button: {
      minWidth: 0,
      width: 36,
      height: 36
    },
    secondButton: {
      marginLeft: 8
    },
    formControl: {
      height: 50,
      width: 80
    },
    switch: {
      marginLeft: 0,
      marginRight: 0
    },
    switchLabel: {
      fontSize: '0.85rem',
      marginLeft: -8
    },
    container: {
      border: '0.5px solid #eadfbc',
      margin: '16px auto 16px auto',
      height: CONTAINER_HEIGHT,
      width: CONTAINER_WIDTH,
      maxWidth: '95%',
      display: 'grid',
      gridTemplateRows,
      gridTemplateColumns,
      placeItems: 'center',
      touchAction: 'none'
    },
    footer: {
      marginBottom: 16
    }
  })
}

export const Container = (props: ContainerProps): JSX.Element => {
  const containerSharedProps = useContainer(props)
  const {
    grid, rowNumber, columnNumber, terminalCellDict,
    lineSourceCellDict, lineDestinationCellDict, toggleAnswer,
    dialogTitle, dialogContent, dialogOpened
  } = containerSharedProps.state
  const {
    onMouseDown, onMouseUp, onMouseMove,
    onTouchStart, onTouchMove, onTouchEnd,
    onMouseLeave, handleToggleAnswer, openDialogForChangeGridSize,
    openDialogForNewGame, openDialogForResetGame,
    handleDialogYesButton, handleDialogNoButton
  } = containerSharedProps.operation

  const useStyles = React.useCallback(createStyle(rowNumber, columnNumber),
    [rowNumber, columnNumber])
  const classes = useStyles()

  return <div className={classes.root}>
    <div className={classes.controlWrapper}>
      <div>
        <FormControl className={classes.formControl}>
          <Select value={rowNumber} onChange={openDialogForChangeGridSize} disabled={toggleAnswer}>
            {GRID_SIZE.map((size: number) => <MenuItem key={size} value={size}>
              {`${size} x ${size}`}
            </MenuItem>)}
          </Select>
          <FormHelperText>Board Size</FormHelperText>
        </FormControl>
      </div>
      <div>
        <Button className={classes.button} variant='outlined'
          color='primary' onClick={openDialogForNewGame} disabled={toggleAnswer} >
          <AddIcon />
        </Button>
        <Button className={clsx(classes.button, classes.secondButton)} variant='outlined'
          color='primary' onClick={openDialogForResetGame} disabled={toggleAnswer} >
          <AutorenewIcon />
        </Button>
        <FormControlLabel className={classes.switch}
          classes={
            { label: classes.switchLabel }
          }
          label='Answer'
          control={<Switch checked={toggleAnswer} onChange={handleToggleAnswer} color='primary' />}
        />
      </div>
    </div>

    <div className={classes.container}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}>
      <Grid grid={grid}
        terminalCellDict={terminalCellDict}
        lineSourceCellDict={lineSourceCellDict}
        lineDestinationCellDict={lineDestinationCellDict} />
    </div>
    <Typography className={classes.footer} variant='body2'>
      Programmed by <a href='https://www.linkedin.com/in/teguh-suryo-santoso/'>
        Teguh Suryo Santoso
        </a>
    </Typography>
    <ResponsiveDialog title={dialogTitle}
      content={dialogContent}
      opened={dialogOpened}
      onYesClicked={handleDialogYesButton}
      onNoClicked={handleDialogNoButton} />
  </div >
}
